import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/lib/cn';

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

type TooltipPosition = NonNullable<TooltipProps['position']>;

const OFFSET_PX = 10;

const arrowPositions: Record<TooltipPosition, string> = {
  top: '-bottom-1 left-1/2 -translate-x-1/2',
  bottom: '-top-1 left-1/2 -translate-x-1/2',
  left: '-right-1 top-1/2 -translate-y-1/2',
  right: '-left-1 top-1/2 -translate-y-1/2',
};

function getTooltipStyle(
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  position: TooltipPosition,
): CSSProperties {
  let style: CSSProperties;

  switch (position) {
    case 'bottom':
      style = {
        top: triggerRect.bottom + OFFSET_PX,
        left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
      };
      break;
    case 'left':
      style = {
        top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
        left: triggerRect.left - tooltipRect.width - OFFSET_PX,
      };
      break;
    case 'right':
      style = {
        top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
        left: triggerRect.right + OFFSET_PX,
      };
      break;
    case 'top':
    default:
      style = {
        top: triggerRect.top - tooltipRect.height - OFFSET_PX,
        left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
      };
      break;
  }

  const viewportPadding = 8;
  const maxLeft = window.innerWidth - tooltipRect.width - viewportPadding;
  const clampedLeft = Math.max(
    viewportPadding,
    Math.min(typeof style.left === 'number' ? style.left : 0, maxLeft),
  );

  return {
    ...style,
    left: clampedLeft,
  };
}

export function Tooltip({
  content,
  children,
  position = 'top',
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({
    top: 0,
    left: 0,
    visibility: 'hidden',
  });

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) {
      return;
    }

    const nextStyle = getTooltipStyle(
      trigger.getBoundingClientRect(),
      tooltip.getBoundingClientRect(),
      position,
    );
    setStyle({
      ...nextStyle,
      visibility: 'visible',
    });
  }, [position]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle({ top: 0, left: 0, visibility: 'hidden' });
      return;
    }

    updatePosition();

    const handleScrollOrResize = () => {
      close();
    };

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) {
        close();
        return;
      }
      if (triggerRef.current?.contains(target)) {
        // Clicking the trigger (e.g. opening a menu) should dismiss the tooltip.
        close();
        return;
      }
      close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [close, open, updatePosition]);

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, []);

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={() => {
        setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
    >
      {children}

      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            style={style}
            className={cn(
              'pointer-events-none fixed z-[9999] min-w-[8.5rem] whitespace-nowrap rounded-[6px] bg-surface px-4 py-2.5 text-center text-xs font-medium text-white shadow-lg',
              className,
            )}
          >
            {content}

            <span
              className={cn(
                'absolute h-3 w-3 rotate-45 border-0 bg-surface',
                arrowPositions[position],
              )}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
